import { Theme as AugmentedTheme, useMediaQuery, useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  APIActions,
  ApiButton,
  Avatar,
  Box,
  Button,
  CustomDialog,
  Grid,
  Icon,
  KeyValue,
  Typography,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { convertMB2Byte } from '../../../../../utils/FileUtils';
import LoadingIconImage from '../../../../../assets/Loading_icon.gif';

import {typeTheme} from "web-library/theme/helper/tssHelper"
const useStyles = makeStyles()((theme) =>
({
  input: {
    marginBottom: theme.spacing(4),
  },
  keyValue: {
    wordWrap: 'break-word',
  },
  formElementContainer: {
    marginBottom: theme.spacing(4),
  },
  link: {
     ...typeTheme(theme, 'buttonSmall'),
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  dividerContainer: {
    paddingTop: theme.spacing(2),
  },
  formHeader: {
    width: '100%',
  },
  profileAvatar: {
    width: theme.spacing(15.5),
    height: theme.spacing(15.5),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      width: theme.spacing(15),
      height: theme.spacing(15),
    },
  },
  profilePicContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'center',
  },
  profileTextEdit: {
    paddingTop: theme.spacing(3),
    paddingLeft: theme.spacing(3),
  },
  profilePicContainerWebView: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    textAlign: 'center',
  },
  profileButton: {
    paddingLeft: theme.spacing(5),
    alignContent: 'center',
    justifyContent: 'center',
  },
  alert: {
    backgroundColor: theme.color.background.bgAlt,
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(0),
    marginBottom: theme.spacing(1.5),
  },
  editIcon: {
    verticalAlign: 'sub',
    color: '#1473DA',
    alignSelf: 'center',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    justifyContent: 'flex-start', // NOSONAR
    maxHeight: 70,
    minHeight: 70,
    alignItems: 'center',
    borderRadius: 16,

    '&.MuiAvatar-root.MuiAvatar-circular': {
      backgroundColor: theme.color.background.bgMain,
      border: 'none',
      borderRadius: '18px',
      padding: '6px 12px',
      marginLeft: theme.spacing(1),
      marginRight: '0',
      marginBottom: theme.spacing(7),
      '& p': {
        color: [theme.color.pill.pillInfoText, '!important'],
        fontSize: '14px',
      },
    },
  },
  textContainer: {
    justifyContent: 'flex-start', // NOSONAR
    display: 'flex',

    flexDirection: 'column',
    alignItems: 'flex-start',

    '& h6': {
      fontSize: 14,
      color: '#96A1AB',
    },
  },
  webViewTextMarginLeft: {
    marginLeft: 16,
  },
  cancelProfileChange: {
    color: '#E55934',
  },
}));

export interface Props {
  photoUrl: string | undefined;
  photoUuid: string | undefined;
  isEditable: boolean;
  personnelType: string | undefined;
  fileUploadErrorInd: boolean;
  setFileProcessingInd: any;
  setFileUploadErrorInd: any;
  fileUploadError: string;
  setFileUploadError: any;
  setUploadedFiles: any;
  acceptableExtension: string;
  statutoryName: string | undefined;
  updateFormState: any;
  loading: boolean;
  setFileUuid: any;
  setLoading: any;
  changeProfileText: any;
  createNewProfileInd: boolean;
  isPersonnelProfileInd: boolean;
  fileUploadStatus?: string;
  setFileUrl: any;
}

const GridKeyValue = ({
  label,
  value,
}: {
  label: string;
  value: string | string[] | undefined;
}) => (
  <Grid xl={5} lg={6} md={12} sm={12} xs={12}>
    <KeyValue keyText={label} valueText={value || '-'} />
  </Grid>
);

function ProfilePicture({
  photoUrl,
  photoUuid,
  fileUploadErrorInd,
  isEditable,
  personnelType,
  setFileProcessingInd,
  setFileUploadErrorInd,
  setUploadedFiles,
  acceptableExtension,
  statutoryName,
  updateFormState,
  loading,
  setLoading,
  setFileUuid,
  changeProfileText,
  createNewProfileInd,
  isPersonnelProfileInd,
  fileUploadError,
  setFileUploadError,
  fileUploadStatus = '',
  setFileUrl,
}: Props) {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const inputRef = useRef<any>(null);
  const theme = useTheme();
  const maxSize = convertMB2Byte(10);
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));
  const [openDialog, setOpenDialog] = useState(false);
  const [oldProfileUrl, setOldProfileUrl] = useState('');
  const [oldProfileUuid, setOldProfileUUid] = useState('');
  const openProfilePictureUploadBox = () => {
    if (inputRef !== null && inputRef.current !== null) inputRef.current.click();
  };

  const handleDialogPopUp = () => {
    setOpenDialog(!openDialog);
  };

  const handleCancelPhotoUpload = () => {
    setOpenDialog(!openDialog);
    setLoading(!loading);
    setFileProcessingInd(false);
    setFileUrl(oldProfileUrl);
    setFileUuid(oldProfileUuid);
    setUploadedFiles([]);
  };
  const profilePictureChangeHandler = (ev: any) => {
    if (ev.target.files[0]) {
      setFileProcessingInd(true);
      setLoading(true);
      setOldProfileUrl(photoUrl || '');
      setOldProfileUUid(photoUuid || '');
      const fileName: string = ev.target.files[0].name || '';
      const fileNameSplit: string[] = fileName.split('.');
      const acceptableExtensionArray = acceptableExtension.split(',');
      const fileExtension = fileNameSplit[fileNameSplit.length - 1].toLowerCase();

      const fileSizeIsExceeded = ev.target.files[0].size > maxSize;
      if (checkAnyFileError(fileSizeIsExceeded, acceptableExtensionArray, fileExtension)) {
        if (fileSizeIsExceeded) {
          setFileUploadError('File Exceed Maximum Size.');
        }
        if (!acceptableExtensionArray.includes(`.${fileExtension}`)) {
          setFileUploadError('File Extension not correct.');
        }
        setFileProcessingInd(false);
        setFileUploadErrorInd(true);
        setLoading(false);
      } else {
        setFileUploadError('');
        setFileUploadErrorInd(false);
        setUploadedFiles(ev.target.files);
      }
    }
  };

  const checkAnyFileError = (
    fileSizeIsExceeded: boolean,
    acceptableExtensionArray: string[],
    fileExtension: string
  ) => fileSizeIsExceeded || !acceptableExtensionArray.includes(`.${fileExtension}`);

  const getPhotoUrl = () => (loading ? LoadingIconImage : photoUrl);

  if (isEditable) {
    return (<>
      {loading && (
        <Grid container>
          <Grid xs={12}>
            <CustomDialog
              open={openDialog}
              title={t('admin->>CreateOpsAdminForm.Cancel Upload?')}
              message={t('admin->>CreateOpsAdminForm.cancelUploadContent')}
              action={
                <APIActions
                  onClose={handleDialogPopUp}
                  apiCall={handleCancelPhotoUpload}
                  cancelString={t('admin->>CreateOpsAdminForm.Cancel')}
                  apiString={t('admin->>CreateOpsAdminForm.Cancel Upload')}
                />
              }
            />

            {isMobileView ? (
              <Box className={classes.profilePicContainer}>
                <Avatar src={LoadingIconImage} className={classes.profileAvatar} type="image" />
                <div className={classes.profilePicContainer}>{fileUploadStatus}</div>
                <Button type="button" variant="text" color="primary" onClick={handleDialogPopUp}>
                  <div className={classes.cancelProfileChange}>X Cancel Upload</div>
                </Button>
              </Box>
            ) : (
              <Box className={classes.profilePicContainerWebView}>
                <Avatar src={LoadingIconImage} className={classes.profileAvatar} type="image" />
                <div className={classes.profilePicContainer}>{fileUploadStatus}</div>
                <Button type="button" variant="text" color="primary" onClick={handleDialogPopUp}>
                  <div className={classes.cancelProfileChange}>X Cancel Upload</div>
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      )}
      {!loading && (
        <Grid container>
          <Grid xs={12}>
            {isMobileView ? (
              <>
                <Box className={classes.profilePicContainer}>
                  <Avatar src={getPhotoUrl()} className={classes.profileAvatar} type="image" />
                </Box>
                <Grid container justify="center">
                  <Button
                    type="button"
                    variant="text"
                    color="primary"
                    onClick={openProfilePictureUploadBox}
                  >
                    {changeProfileText}
                  </Button>
                </Grid>
              </>
            ) : (
              <Box className={classes.profilePicContainerWebView}>
                <Avatar src={photoUrl} className={classes.profileAvatar} type="image" />
                <div className={[classes.profilePicContainer, 'profileButton'].join(' ')}>
                  <Button
                    type="button"
                    variant="text"
                    color="primary"
                    onClick={openProfilePictureUploadBox}
                  >
                    {changeProfileText}
                  </Button>
                </div>
              </Box>
            )}
          </Grid>
          {!createNewProfileInd && isPersonnelProfileInd && (
            <Grid container className={classes.profileTextEdit}>
              <GridKeyValue
                label={t('components->>CompleteAdminProfileDetails.Ep Type')}
                value={personnelType}
              />
            </Grid>
          )}
        </Grid>
      )}
      <div>
        <input
          type="file"
          style={{ display: 'none' }}
          ref={inputRef}
          name="profile_picture"
          onChange={profilePictureChangeHandler}
          accept={acceptableExtension}
        />
      </div>
      <div>
        <input
          value={photoUuid}
          onChange={setFileUuid}
          type="text"
          style={{ display: 'none' }}
          id="photoUUid"
        />
      </div>
      {fileUploadErrorInd && (
        <Alert
          className={classes.alert}
          message={fileUploadError}
          severity="error"
          iconColor={theme.color.background.bgInverse01}
          iconSize="M"
        />
      )}
    </>);
  }
  return (<>
    {isMobileView ? (
      <>
        <Grid container justify="flex-end" alignItems="center">
          <Grid>
            <Icon name={IconName.edit} className={classes.editIcon} size="M" />
          </Grid>
          <Grid>
            <ApiButton
              type="button"
              variant="text"
              color="primary"
              onClick={updateFormState}
              status="idle"
            >
              {t('components->>CompleteAdminProfileDetails.Edit Profile')}
            </ApiButton>
          </Grid>
        </Grid>
        <Grid container justify="center">
          <Grid xs={12}>
            <Box className={classes.profilePicContainer}>
              <Avatar className={classes.profileAvatar} src={photoUrl} />
            </Box>
          </Grid>

          <div className={classes.textContainer}>
            <Typography variant="body1" color="inherit">
              {statutoryName}
            </Typography>
            <Typography variant="subtitle2" color="inherit">
              {personnelType}
            </Typography>
          </div>
        </Grid>
      </>
    ) : (
      <Grid container>
        <Box className={classes.profilePicContainer}>
          <Avatar className={classes.profileAvatar} src={photoUrl} type="image" />
          <div className={[classes.textContainer, classes.webViewTextMarginLeft].join(' ')}>
            <Typography variant="body1" color="inherit">
              {statutoryName}
            </Typography>
            {isPersonnelProfileInd && (
              <Typography variant="subtitle2" color="inherit">
                {personnelType}
              </Typography>
            )}
          </div>
        </Box>
        <div className={[classes.profilePicContainer, classes.profileButton].join(' ')}>
          <Grid>
            <Icon name={IconName.edit} className={classes.editIcon} size="M" />
          </Grid>
          <Grid>
            <Button type="button" variant="text" color="primary" onClick={updateFormState}>
              {t('components->>CompleteAdminProfileDetails.Edit Profile')}
            </Button>
          </Grid>
        </div>
      </Grid>
    )}
  </>);
}

export default ProfilePicture;
